/**
 * Loyalty money spent on an order — a cashback balance, a loyalty reward coupon, or whatever kind of
 * redemption munchies-api adds next — normalised into ONE view model every surface renders from.
 *
 * Two wire shapes reach the UI:
 *   - the order's frozen snapshot (`Order.loyaltyDiscount`, `GET /orders/:id`), and
 *   - the operator cart view's projection of it (`CartView.loyaltyDiscount`, `/orders/:id/draft`).
 * Both are read only through {@link loyaltyRedemptionOf} / {@link loyaltyRedemptionOfCart}, so the
 * totals math and the "which kind is this" decision live here and nowhere else.
 *
 * Money rule: the backend folds the loyalty line into `discountAmount` (items share, never the
 * fee), while `appliedDiscounts` (coupons, promotions) never contains it. A surface that prints the
 * coupons and the loyalty line separately must therefore take the loyalty amount out of the items
 * discount — {@link itemsDiscountExcludingLoyalty} is that subtraction.
 */

/**
 * The reward kinds munchies-api can hold against an order. Open on purpose: a type this build does
 * not know yet must still render (as a generic redemption) rather than fail to type-check or vanish.
 */
export type LoyaltyRewardType = 'COUPON' | 'BALANCE' | (string & {});

/** The loyalty redemption frozen onto an order (`Order.loyaltyDiscount`). */
export interface LoyaltyRedemption {
  /** munchies-api's id for the reservation (the hold). */
  reservationId: string;
  rewardType: LoyaltyRewardType;
  /** What the hold promises, major units. */
  amount: number;
  /** {@link amount} in integer minor units, as munchies-api reported it. */
  amountMinor: number;
  currency: string;
  /** ISO — when the hold lapses if the order is never placed. */
  expiresAt: string;
  /** What the discount line actually took off, major units. Absent on older orders → {@link amount}. */
  appliedAmount?: number;
  /** ISO — set once munchies-api settled (consumed) the hold. */
  consumedAt?: string;
  loyaltyCustomerId?: string;
  /** munchies-api `loyalty_transactions` id of the settlement. */
  transactionId?: string;
  /** COUPON only — the loyalty reward that was redeemed. */
  rewardId?: string;
}

/** The operator cart view's projection of the same redemption (`CartView.loyaltyDiscount`). */
export interface CartLoyaltyRedemption {
  rewardType: LoyaltyRewardType;
  /** What the hold promises. */
  amount: number;
  /** What it actually takes off this cart (the snapshot's `appliedAmount`). */
  discountAmount: number;
  currency: string;
  expiresAt: string;
  reservationId?: string;
  transactionId?: string;
  loyaltyCustomerId?: string;
  consumedAt?: string;
}

/** Which family of redemption — decides the label and the icon. `other` = a type this build predates. */
export type LoyaltyRedemptionKind = 'balance' | 'coupon' | 'other';

/**
 * Where the loyalty money stands:
 * - `held` — reserved upstream, not spent yet (the order has not settled);
 * - `settled` — consumed by munchies-api (`consumedAt` set);
 * - `released` — never settled and the order is dead (cancelled / retired draft), so the hold was
 *   given back to the customer.
 */
export type LoyaltyRedemptionStatus = 'held' | 'settled' | 'released';

export interface LoyaltyRedemptionView {
  kind: LoyaltyRedemptionKind;
  /** The raw type, for a tooltip or debugging an `other`. */
  rewardType: string;
  /** What came off the order — the figure every total adds up with. */
  appliedAmount: number;
  /** What the hold promised; larger than {@link appliedAmount} when the cart could not absorb it. */
  heldAmount: number;
  currency: string;
  status: LoyaltyRedemptionStatus;
  settledAt?: string;
  expiresAt?: string;
  transactionId?: string;
  reservationId?: string;
  /** The redemption's own customer, else the order's (card-paid orders). */
  loyaltyCustomerId?: string;
  rewardId?: string;
}

/** Order statuses whose unsettled hold has been (or will be) released back to the customer. */
const DEAD_ORDER_STATUSES = new Set(['cancelled', 'abandoned', 'superseded']);

export function loyaltyRedemptionKind(rewardType: string | null | undefined): LoyaltyRedemptionKind {
  if (rewardType === 'BALANCE') return 'balance';
  if (rewardType === 'COUPON') return 'coupon';
  return 'other';
}

/** `orders` namespace key naming a redemption kind. */
export function loyaltyRedemptionLabelKey(kind: LoyaltyRedemptionKind): string {
  return `loyalty.kind.${kind}`;
}

/** `orders` namespace key naming a redemption status. */
export function loyaltyRedemptionStatusKey(status: LoyaltyRedemptionStatus): string {
  return `loyalty.status.${status}`;
}

/**
 * The money a redemption took off, from either wire shape: the applied figure when the payload
 * carries it, else the held amount (an order snapshot written before `appliedAmount` existed).
 * 0 for none, never negative.
 */
export function loyaltyAppliedAmount(source: LoyaltyRedemption | CartLoyaltyRedemption | null | undefined): number {
  if (!source) return 0;
  const applied = 'discountAmount' in source ? source.discountAmount : (source.appliedAmount ?? source.amount);
  const value = Number(applied);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function statusOf(consumedAt: string | undefined, orderStatus: string | undefined): LoyaltyRedemptionStatus {
  if (consumedAt) return 'settled';
  if (orderStatus && DEAD_ORDER_STATUSES.has(orderStatus)) return 'released';
  return 'held';
}

/** The order's loyalty redemption as a view model, or null when it spent none. */
export function loyaltyRedemptionOf(order: {
  loyaltyDiscount?: LoyaltyRedemption | null;
  loyaltyCustomerId?: string;
  status?: string;
}): LoyaltyRedemptionView | null {
  const snapshot = order.loyaltyDiscount;
  if (!snapshot) return null;
  return {
    kind: loyaltyRedemptionKind(snapshot.rewardType),
    rewardType: snapshot.rewardType,
    appliedAmount: loyaltyAppliedAmount(snapshot),
    heldAmount: Number(snapshot.amount) || 0,
    currency: snapshot.currency,
    status: statusOf(snapshot.consumedAt, order.status),
    settledAt: snapshot.consumedAt,
    expiresAt: snapshot.expiresAt,
    transactionId: snapshot.transactionId,
    reservationId: snapshot.reservationId,
    loyaltyCustomerId: snapshot.loyaltyCustomerId ?? order.loyaltyCustomerId,
    rewardId: snapshot.rewardId,
  };
}

/** The operator cart's loyalty redemption as a view model, or null when none is on the cart. */
export function loyaltyRedemptionOfCart(cart: {
  loyaltyDiscount?: CartLoyaltyRedemption | null;
  status?: string;
}): LoyaltyRedemptionView | null {
  const line = cart.loyaltyDiscount;
  if (!line) return null;
  return {
    kind: loyaltyRedemptionKind(line.rewardType),
    rewardType: line.rewardType,
    appliedAmount: loyaltyAppliedAmount(line),
    heldAmount: Number(line.amount) || 0,
    currency: line.currency,
    status: statusOf(line.consumedAt, cart.status),
    settledAt: line.consumedAt,
    expiresAt: line.expiresAt,
    transactionId: line.transactionId,
    reservationId: line.reservationId,
    loyaltyCustomerId: line.loyaltyCustomerId,
  };
}

/** True when the hold promised more than the order could absorb (a coupon larger than the cart). */
export function isPartialRedemption(view: Pick<LoyaltyRedemptionView, 'appliedAmount' | 'heldAmount'>): boolean {
  return view.heldAmount - view.appliedAmount > 0.005;
}

/**
 * The items discount that is NOT the loyalty line: `discountAmount − fee share − loyalty`. What a
 * surface printing coupons and loyalty as separate rows shows on its generic discount row.
 */
export function itemsDiscountExcludingLoyalty(
  totals: { discountAmount?: number | null; deliveryFeeDiscountAmount?: number | null },
  redemption: Pick<LoyaltyRedemptionView, 'appliedAmount'> | null,
): number {
  const items = Number(totals.discountAmount ?? 0) - Number(totals.deliveryFeeDiscountAmount ?? 0);
  return Math.max(0, items - (redemption?.appliedAmount ?? 0));
}

/** First 8 characters of an id — enough to match against the dashboard, short enough for a ticket. */
export function shortTraceId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

/** The dashboard page of a loyalty customer — the same route the order payment section links to. */
export function loyaltyCustomerDashboardUrl(dashboardUrl: string, loyaltyCustomerId: string): string {
  return `${dashboardUrl}/loyalty/customers/${loyaltyCustomerId}`;
}
